import { FocusEvent, KeyboardEvent, MouseEvent, ReactNode, useMemo, useState } from "react";
import { DROPDOWN_V2_CLEAR_VALUE, DropdownV2Props } from "../Dropdown/DropdownV2";
import { OptionV2 } from "../types";
import { formatErrorMessageV2 } from "../utils";

export type MultiDropdownV2Props<T = any> = Omit<DropdownV2Props<T>, "value" | "onChange"> & {
  value?: OptionV2<T>[];
  onChange?: (options: OptionV2<T>[]) => void;
  renderOption?: (option: OptionV2<T>) => ReactNode;
  selectAll?: boolean;
  selectAllLabel?: string;
};

const SELECT_ALL_VALUE = "__SELECT_ALL__";

export function MultiDropdownV2<T = any>({
  id,
  name,
  label,
  description = "",
  value = [],
  options = [],
  placeholder = "Selecione...",
  disabled = false,
  searchable = false,
  error,
  emptyMessage = "Nenhuma opção disponível",
  menuMount = "static",
  clearOptionLabel,
  onChange,
  onBlur,
  renderOption,
  selectAll = false,
  selectAllLabel = "Selecionar Todos",
}: MultiDropdownV2Props<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selectId = id || `onboarding-v2-${name}`;

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return options;
    return options.filter((option) => option.label.toLowerCase().includes(normalizedSearch));
  }, [options, search]);

  const allSelected = useMemo(
    () =>
      options.length > 0 &&
      options.every((option) =>
        value.some((selected) => String(selected.value) === String(option.value)),
      ),
    [options, value],
  );

  const showSelectAll = selectAll && options.length > 1 && !allSelected;

  const menuOptions = useMemo(() => {
    const base = showSelectAll
      ? [
        {
          label: selectAllLabel,
          value: SELECT_ALL_VALUE as unknown as T,
          info: { isSelectAll: true },
        } as OptionV2<T>,
        ...filteredOptions,
      ]
      : filteredOptions;
    if (!clearOptionLabel) return base;
    const clearOption: OptionV2<T> = {
      label: clearOptionLabel,
      value: DROPDOWN_V2_CLEAR_VALUE as unknown as T,
      info: { __clearSelection: true },
    };
    return [clearOption, ...base];
  }, [showSelectAll, selectAllLabel, filteredOptions, clearOptionLabel]);

  function isSelected(option: OptionV2<T>) {
    return value.some((selected) => String(selected.value) === String(option.value));
  }

  function isSelectAllOption(option: OptionV2<T>) {
    return String(option.value) === SELECT_ALL_VALUE;
  }

  function isClearOption(option: OptionV2<T>) {
    return Boolean(clearOptionLabel) && String(option.value) === DROPDOWN_V2_CLEAR_VALUE;
  }

  function handleToggle() {
    if (!disabled) setIsOpen((current) => !current);
  }

  function handleSelect(option: OptionV2<T>) {
    if (isClearOption(option)) {
      onChange && onChange([]);
      setSearch("");
      return;
    }
    if (isSelectAllOption(option)) {
      onChange && onChange(options);
      setSearch("");
      return;
    }
    const nextValue = isSelected(option)
      ? value.filter((selected) => String(selected.value) !== String(option.value))
      : [...value, option];
    onChange && onChange(nextValue);
  }

  function handleRemoveSelected(event: MouseEvent<HTMLButtonElement>, option: OptionV2<T>) {
    event.preventDefault();
    event.stopPropagation();
    onChange && onChange(value.filter((selected) => String(selected.value) !== String(option.value)));
  }

  function handleOptionMouseDown(event: MouseEvent<HTMLButtonElement>) {
    // Safari may report a null relatedTarget while moving focus to a menu option.
    // Keeping focus on the trigger prevents the wrapper blur from closing the
    // menu before the option's click event is handled.
    event.preventDefault();
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleToggle();
  }

  function handleTriggerMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (!disabled) return;
    event.preventDefault();
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsOpen(false);
    setSearch("");
    onBlur && onBlur();
  }

  const menuClassName =
    menuMount === "overlay"
      ? "ov2-dropdown__menu ov2-dropdown__menu--overlay"
      : "ov2-dropdown__menu";

  const trigger = (
    <div
      className={`ov2-dropdown__trigger ${isOpen ? "ov2-dropdown__trigger--open" : ""} ${error ? "ov2-input--error" : ""} ${disabled ? "ov2-input--disabled" : ""}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-expanded={isOpen}
      onMouseDown={handleTriggerMouseDown}
      onClick={handleToggle}
      onKeyDown={handleTriggerKeyDown}
    >
      <span className={value.length ? "ov2-dropdown__value ov2-multidropdown__trigger-value" : "ov2-dropdown__placeholder"}>
        {value.length
          ? value.map((option) => (
            <span className="ov2-multidropdown__tag" key={`${option.value}`}>
              <span>{option.label}</span>
              {!disabled && (
                <button
                  className="ov2-multidropdown__tag-remove"
                  type="button"
                  aria-label={`Remover ${option.label}`}
                  onClick={(event) => handleRemoveSelected(event, option)}
                >
                  ×
                </button>
              )}
            </span>
          ))
          : placeholder}
      </span>
      {!disabled && (
        <span className={`ov2-dropdown__chevron ${isOpen ? "ov2-dropdown__chevron--open" : ""}`}>⌃</span>
      )}
    </div>
  );

  function renderMenu() {
    if (!isOpen) return null;
    return (
      <div className={menuClassName}>
        {searchable && (
          <input
            className="ov2-dropdown__search"
            value={search}
            placeholder="Buscar..."
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
        )}
        <ul>
          {menuOptions.length > 0 ? (
            menuOptions.map((option) => {
              const selectAllRow = isSelectAllOption(option);
              const clearRow = isClearOption(option);
              const selected = !selectAllRow && !clearRow && isSelected(option);
              return (
                <li key={`${option.value}`}>
                  <button
                    className={`ov2-multidropdown__option ${selected ? "ov2-multidropdown__option--selected" : ""}`}
                    type="button"
                    onMouseDown={handleOptionMouseDown}
                    onClick={() => handleSelect(option)}
                  >
                    <span className="ov2-multidropdown__checkbox">{selected ? "✓" : ""}</span>
                    {selectAllRow || clearRow || !renderOption ? <span>{option.label}</span> : renderOption(option)}
                  </button>
                </li>
              );
            })
          ) : (
            <li className="ov2-dropdown__empty">{emptyMessage}</li>
          )}
        </ul>
      </div>
    );
  }

  return (
    <div className="ov2-field ov2-dropdown ov2-multidropdown" id={selectId} onBlur={handleBlur}>
      {label && <span className="ov2-field__label">{label}</span>}
      {description && <span className="ov2-field__description">{description}</span>}
      {menuMount === "overlay" ? (
        <div className="ov2-dropdown__anchor">
          {trigger}
          {renderMenu()}
        </div>
      ) : (
        <>
          {trigger}
          {renderMenu()}
        </>
      )}
      {error && <small className="ov2-field__error animation__fadeIn">{formatErrorMessageV2(error)}</small>}
    </div>
  );
}
